import React, { useEffect, useState } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, Link } from 'react-router-dom';
import orderService from '../services/orders';
import api from '../services/api';
import toast from 'react-hot-toast';
import { FiCheckCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const VisaIcon = () => (
    <svg viewBox="0 0 36 24" width="36" height="24" xmlns="http://www.w3.org/2000/svg">
        <rect width="36" height="24" rx="3" fill="#1A1F71"/>
        <text x="18" y="15" fontFamily="Arial, sans-serif" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" dominantBaseline="middle">VISA</text>
    </svg>
);

const MastercardIcon = () => (
    <svg viewBox="0 0 36 24" width="36" height="24" xmlns="http://www.w3.org/2000/svg">
        <rect width="36" height="24" rx="3" fill="white"/>
        <circle cx="13" cy="12" r="7" fill="#EB001B" opacity="0.8"/>
        <circle cx="23" cy="12" r="7" fill="#F79E1B" opacity="0.8"/>
        <path d="M18 16.9c1.3-1.4 2.1-3.1 2.1-4.9s-.8-3.5-2.1-4.9c-1.3 1.4-2.1 3.1-2.1 4.9s.8 3.5 2.1 4.9z" fill="#FF5F00"/>
    </svg>
);

const PAKISTAN_CITIES = [
    "Lahore", "Faisalabad", "Rawalpindi", "Multan", "Gujranwala", "Sialkot", "Bahawalpur",
    "Sargodha", "Sheikhupura", "Jhang", "Rahim Yar Khan", "Kasur", "Gujrat", "Mandi Bahauddin",
    "Chakwal", "Dera Ghazi Khan", "Sahiwal", "Okara", "Pakpattan", "Lodhran", "Khanewal",
    "Burewala", "Vehari", "Kamoke", "Hafizabad", "Jaranwala", "Narowal", "Murree",
    "Karachi", "Hyderabad", "Sukkur", "Larkana", "Nawabshah", "Mirpur Khas", "Dadu",
    "Jamshoro", "Khairpur", "Badin", "Thatta", "Umerkot", "Kashmore", "Ghotki",
    "Peshawar", "Mardan", "Abbottabad", "Swat", "Kohat", "Dera Ismail Khan", "Mansehra",
    "Chitral", "Bannu", "Charsadda", "Nowshera", "Lower Dir", "Upper Dir",
    "Quetta", "Gwadar", "Turbat", "Chaman", "Khuzdar", "Zhob", "Loralai", "Mastung",
    "Kalat", "Nushki", "Pishin", "Kech",
    "Gilgit", "Skardu", "Hunza", "Shigar", "Khaplu", "Ghanche", "Ghizer",
    "Muzaffarabad", "Mirpur", "Rawalakot", "Kotli", "Bhimber", "Bagh", "Hattian Bala",
    "Islamabad"
];

const CheckoutPage = () => {
    const { cartItems, cartTotals, clearCart } = useCart();
    const { user } = useAuth();
    const { theme } = useTheme();
    const navigate = useNavigate();

    const [settings, setSettings] = useState({
        checkoutSidebarColor: '#faf4f8',
        shippingCost: 199,
        freeShippingThreshold: 3500
    });

    const showCardPayment = localStorage.getItem('showCardPayment') !== 'false';

    const [formData, setFormData] = useState({
        email: user?.email || '',
        emailOffers: true,
        cityDropdown: '',
        country: 'Pakistan',
        firstName: user?.name?.split(' ')[0] || '',
        lastName: user?.name?.split(' ').slice(1).join(' ') || '',
        address: user?.address || '',
        apartment: '',
        city: '',
        postalCode: '',
        phone: user?.phone || '',
        saveInfo: false,
        textOffers: false,
        shippingMethod: showCardPayment ? 'prepaid' : 'cod',
        paymentMethod: showCardPayment ? 'card' : 'cod',
        billingAddress: 'same'
    });

    const [billingFormData, setBillingFormData] = useState({
        billingCountry: 'Pakistan',
        billingFirstName: '',
        billingLastName: '',
        billingAddress: '',
        billingApartment: '',
        billingCity: '',
        billingPostalCode: '',
        billingPhone: ''
    });

    const [loading, setLoading] = useState(false);
    const [discountCode, setDiscountCode] = useState('');
    const [discountAmount, setDiscountAmount] = useState(0);
    const [orderPlaced, setOrderPlaced] = useState(false);

    const [receiptFile, setReceiptFile] = useState(null);
    const [uploadingReceipt, setUploadingReceipt] = useState(false);
    const [receiptUrl, setReceiptUrl] = useState('');

    // Mobile summary toggle
    const [showMobileSummary, setShowMobileSummary] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/settings/announcement');
                setSettings({
                    checkoutSidebarColor: res.checkoutSidebarColor || '#faf4f8',
                    shippingCost: res.shippingCost || 199,
                    freeShippingThreshold: res.freeShippingThreshold || 3500
                });
            } catch (err) {
                console.error('Failed to fetch checkout settings', err);
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        if (cartItems.length === 0 && !orderPlaced) {
            navigate('/cart');
        }
    }, [cartItems.length, navigate, orderPlaced]);

    useEffect(() => {
        if (!showCardPayment) {
            setFormData(prev => ({ ...prev, shippingMethod: 'cod', paymentMethod: 'cod' }));
        }
    }, [showCardPayment]);

    useEffect(() => {
        if (formData.shippingMethod === 'cod') {
            setFormData(prev => ({ ...prev, paymentMethod: 'cod' }));
        }
    }, [formData.shippingMethod]);

    useEffect(() => {
        if (formData.paymentMethod === 'banktransfer' || formData.paymentMethod === 'card') {
            setFormData(prev => ({ ...prev, shippingMethod: 'prepaid' }));
        }
    }, [formData.paymentMethod]);

    const subtotal = cartTotals?.subtotal || 0;
    const shippingCost = formData.shippingMethod === 'prepaid' ? 0 : settings.shippingCost;
    const totalAmount = Math.max(0, subtotal + shippingCost - discountAmount);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleBillingChange = (e) => {
        const { name, value } = e.target;
        setBillingFormData({ ...billingFormData, [name]: value });
    };

    const handleApplyCoupon = async () => {
        if (!discountCode.trim()) return toast.error('Please enter a coupon code');
        try {
            const res = await api.post('/coupons/validate', { code: discountCode, cartTotal: subtotal });
            setDiscountAmount(res.discountAmount || 0);
            toast.success('Discount coupon applied successfully!');
        } catch (err) {
            setDiscountAmount(0);
            toast.error(err.response?.data?.message || 'Invalid or expired discount code');
        }
    };

    const handleReceiptUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setReceiptFile(file);
        setUploadingReceipt(true);
        const formData = new FormData();
        formData.append('receipt', file);
        try {
            const res = await api.post('/upload/receipt', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setReceiptUrl(res.receiptUrl);
            toast.success('Receipt uploaded successfully');
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to upload receipt');
            setReceiptFile(null);
        } finally {
            setUploadingReceipt(false);
        }
    };

    const handlePlaceOrder = async () => {
        if (!formData.email || !formData.firstName || !formData.address || !formData.phone || (!formData.cityDropdown && !formData.city)) {
            toast.error('Please fill all required fields');
            return;
        }
        if (formData.paymentMethod === 'banktransfer' && !receiptUrl) {
            toast.error('Please upload the payment receipt');
            return;
        }

        setLoading(true);
        try {
            let billingAddressObj = null;
            if (formData.billingAddress === 'different') {
                billingAddressObj = {
                    name: `${billingFormData.billingFirstName} ${billingFormData.billingLastName}`.trim(),
                    email: formData.email,
                    phone: billingFormData.billingPhone || formData.phone,
                    address: `${billingFormData.billingAddress} ${billingFormData.billingApartment}`.trim(),
                    city: billingFormData.billingCity || formData.city,
                    province: 'Punjab',
                    postalCode: billingFormData.billingPostalCode || formData.postalCode || '00000'
                };
            }

            const orderPayload = {
                shippingAddress: {
                    name: `${formData.firstName} ${formData.lastName}`.trim(),
                    email: formData.email,
                    phone: formData.phone,
                    address: `${formData.address} ${formData.apartment}`.trim(),
                    city: formData.cityDropdown || formData.city,
                    province: 'Punjab',
                    postalCode: formData.postalCode || '00000'
                },
                billingAddress: billingAddressObj,
                paymentMethod: formData.paymentMethod,
                receiptImage: formData.paymentMethod === 'banktransfer' ? receiptUrl : undefined,
                items: cartItems.map(item => ({
                    product: item.productId,
                    quantity: item.quantity,
                    size: item.options?.size || '',
                    color: item.options?.color || '',
                    price: item.price,
                    productName: item.name,
                    productImage: item.image
                })),
                subtotal,
                shippingCost: shippingCost,
                discountAmount,
                tax: 0,
                total: totalAmount,
                isGuest: !user
            };

            const order = await orderService.createOrder(orderPayload);
            await clearCart();
            toast.success('Order placed successfully!');
            setOrderPlaced(true);

            if (user) {
                navigate('/orders');
            } else {
                navigate('/order-confirmation', { state: { orderData: order } });
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to place order');
        } finally {
            setLoading(false);
        }
    };

    if (cartItems.length === 0) return null;

    const bankDetails = {
        title: 'HAZARI',
        accountNumber: '00521005334769',
        iban: 'PK89ALFH0052001005334769',
        swiftCode: 'ALFHPKKAXXX',
        branchName: 'SADIQABAD BRANCH',
        branchCode: '0052',
        bankName: 'Bank Alfalah'
    };

    // Summary content (reused)
    const summaryContent = (
        <div className="w-full max-w-[460px] mx-auto px-4 sm:px-6 lg:pr-12 xl:pr-20 lg:pl-4">
            <div className="space-y-3 mb-4">
                {cartItems.map(item => (
                    <div key={item.id} className="flex items-center justify-between gap-3">
                        <div className="relative">
                            <div className="w-14 h-14 border border-gray-300 rounded-lg overflow-hidden bg-white relative">
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                            </div>
                            <span className="absolute -top-2 -right-2 bg-gray-500/90 text-white text-[11px] font-medium w-4 h-4 flex items-center justify-center rounded-full">{item.quantity}</span>
                        </div>
                        <div className="flex-1">
                            <p className="text-[14px] font-medium text-gray-900 leading-tight">{item.name}</p>
                            <p className="text-[12px] text-gray-500 mt-0.5">{item.options?.color || ''} {item.options?.color && item.options?.size ? '/' : ''} {item.options?.size || ''}</p>
                        </div>
                        <span className="text-[14px] font-medium text-gray-900 whitespace-nowrap">Rs {(item.price * item.quantity).toLocaleString()}.00</span>
                    </div>
                ))}
            </div>

            <div className="flex gap-2 mb-4 py-3 border-y border-gray-300/60">
                <input type="text" placeholder="Discount code" value={discountCode} onChange={(e) => setDiscountCode(e.target.value)} className="flex-1 border border-[#d9d9d9] rounded-[3px] p-2 text-[14px] bg-white outline-none focus:border-[#e81e82] focus:ring-1 focus:ring-[#e81e82] transition-colors" />
                <button onClick={handleApplyCoupon} className={`px-4 rounded-[3px] text-[14px] font-medium transition-colors ${discountCode ? 'bg-gray-200 text-gray-800 cursor-pointer' : 'bg-[#f4e6ef] text-[#b3a1ac] cursor-not-allowed border border-[#ebdce5]'}`}>Apply</button>
            </div>

            <div className="space-y-2 text-[14px] text-gray-600 mb-4">
                <div className="flex justify-between items-center"><span>Subtotal <span className="text-gray-400 ml-1">• {cartTotals.itemsCount} items</span></span><span className="font-medium text-gray-900">Rs {subtotal.toLocaleString()}.00</span></div>
                {discountAmount > 0 && <div className="flex justify-between items-center text-green-600"><span>Discount Coupon</span><span>-Rs {discountAmount.toLocaleString()}.00</span></div>}
                <div className="flex justify-between items-center">
                    <span className="flex items-center gap-1">Shipping</span>
                    <span className="font-medium text-gray-900">
                        {formData.shippingMethod === 'prepaid' ? 'FREE' : `Rs ${settings.shippingCost}.00`}
                    </span>
                </div>
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-gray-300/60">
                <span className="text-[16px] font-medium text-gray-900">Total</span>
                <div className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-500">PKR</span>
                    <span className="text-[24px] font-medium text-gray-900">
                        Rs {totalAmount.toLocaleString()}.00
                    </span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen flex flex-col bg-white lg:h-screen lg:overflow-hidden">
            <header className="flex-shrink-0 w-full bg-white border-b border-gray-200 py-4 px-4 sm:px-6 lg:px-20 xl:px-32 flex justify-between items-center z-10">
                <Link to="/" className="inline-block">
                    <img src="/logo.png" alt="ArshiVastra Logo" className="h-9 w-auto object-contain" />
                </Link>
                <Link to="/cart" className="relative group">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 transition-colors" style={{ color: theme.primaryColor }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                    </svg>
                    {cartTotals.itemsCount > 0 && (
                        <span className="absolute bottom-[1px] right-[1px] w-2 h-2 rounded-full" style={{ backgroundColor: theme.primaryColor }} />
                    )}
                </Link>
            </header>

            <div className="flex-1 flex flex-col lg:flex-row w-full lg:overflow-hidden">
                <div className="w-full lg:w-[55%] px-4 sm:px-6 md:px-8 lg:px-10 xl:px-16 py-6 bg-white overflow-y-auto checkout-left-scroll">
                    <div className="w-full max-w-[560px] mx-auto">
                        {/* Mobile summary toggle */}
                        <div className="lg:hidden mb-6">
                            <button
                                onClick={() => setShowMobileSummary(!showMobileSummary)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-[#faf4f8] border border-gray-200 rounded-lg"
                                style={{ backgroundColor: settings.checkoutSidebarColor }}
                            >
                                <span className="font-medium text-gray-900">Order Summary</span>
                                <span className="text-gray-600">
                                    {showMobileSummary ? <FiChevronUp size={20} /> : <FiChevronDown size={20} />}
                                </span>
                            </button>
                            {showMobileSummary && (
                                <div className="mt-4 bg-[#faf4f8] border border-gray-200 rounded-lg py-4 px-2" style={{ backgroundColor: settings.checkoutSidebarColor }}>
                                    {summaryContent}
                                </div>
                            )}
                        </div>

                        <div className="space-y-6">
                            <section>
                                <div className="flex justify-between items-center mb-2">
                                    <h2 className="text-[16px] font-medium text-gray-900">Contact</h2>
                                </div>
                                <div className="space-y-2">
                                    <div className="relative">
                                        <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none transition-all peer" />
                                        <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Email</label>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer mt-1"><input type="checkbox" name="emailOffers" checked={formData.emailOffers} onChange={handleChange} className="w-[14px] h-[14px] border-gray-300 rounded text-blue-600 focus:ring-blue-500 cursor-pointer" /><span className="text-[12px] text-gray-700">Email me with news and offers</span></label>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-[16px] font-medium text-gray-900 mb-2">Delivery</h2>
                                <div className="space-y-2">
                                    <div>
                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <div className="relative flex-1">
                                                <label className="absolute top-[4px] left-3 text-[9px] text-gray-500 pointer-events-none">City</label>
                                                <select
                                                    name="cityDropdown"
                                                    value={formData.cityDropdown}
                                                    onChange={handleChange}
                                                    className="w-full border border-[#e32c2b] rounded-[3px] pt-[18px] pb-2 px-3 text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none appearance-none bg-white"
                                                >
                                                    <option value="">Select city from dropdown</option>
                                                    {PAKISTAN_CITIES.map((city) => (
                                                        <option key={city} value={city}>{city}</option>
                                                    ))}
                                                </select>
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"><svg className="w-2.5 h-2.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg></div>
                                            </div>
                                            <button className="w-[36px] flex items-center justify-center bg-[#fdf2f7] border border-[#f8cbe0] rounded-[3px] text-[#e81e82]"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.602 10.602Z" /></svg></button>
                                        </div>
                                        {!formData.cityDropdown && <p className="text-[#e32c2b] text-[11px] mt-1">Please select city from dropdown</p>}
                                    </div>

                                    <div className="relative">
                                        <label className="absolute top-[4px] left-3 text-[9px] text-gray-500 pointer-events-none">Country/Region</label>
                                        <select name="country" value={formData.country} onChange={handleChange} className="w-full border border-[#d9d9d9] rounded-[3px] pt-[18px] pb-2 px-3 text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none appearance-none bg-white"><option value="Pakistan">Pakistan</option></select>
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"><svg className="w-2.5 h-2.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg></div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="relative">
                                            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                            <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">First name</label>
                                        </div>
                                        <div className="relative">
                                            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                            <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Last name</label>
                                        </div>
                                    </div>

                                    <div className="relative">
                                        <input type="text" name="address" value={formData.address} onChange={handleChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                        <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Address</label>
                                    </div>

                                    <div className="relative">
                                        <input type="text" name="apartment" value={formData.apartment} onChange={handleChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                        <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Apartment, suite, etc. (optional)</label>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <div className="relative">
                                                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder=" " className={`w-full border rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all ${!formData.city ? 'border-[#e32c2b]' : 'border-[#d9d9d9]'}`} />
                                                <label className={`absolute left-3 top-[4px] text-[9px] transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none ${!formData.city ? 'text-[#e32c2b]' : 'text-gray-500'}`}>City</label>
                                            </div>
                                            {!formData.city && <p className="text-[#e32c2b] text-[11px] mt-1">Please select city from city dropdown</p>}
                                        </div>
                                        <div className="relative">
                                            <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                            <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Postal code (optional)</label>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="relative">
                                            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder=" " className={`w-full border rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all ${!formData.phone ? 'border-[#e32c2b]' : 'border-[#d9d9d9]'}`} />
                                            <label className={`absolute left-3 top-[4px] text-[9px] transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none ${!formData.phone ? 'text-[#e32c2b]' : 'text-gray-500'}`}>Phone</label>
                                        </div>
                                        {!formData.phone && <p className="text-[#e32c2b] text-[11px] mt-1">Enter a phone number to use this delivery method</p>}
                                    </div>

                                    <div className="space-y-1 mt-2">
                                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" name="saveInfo" checked={formData.saveInfo} onChange={handleChange} className="w-[14px] h-[14px] border-gray-300 rounded text-[#e81e82] focus:ring-[#e81e82] cursor-pointer" /><span className="text-[12px] text-gray-700">Save this information for next time</span></label>
                                        <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" name="textOffers" checked={formData.textOffers} onChange={handleChange} className="w-[14px] h-[14px] border-gray-300 rounded text-[#e81e82] focus:ring-[#e81e82] cursor-pointer" /><span className="text-[12px] text-gray-700">Text me with news and offers</span></label>
                                    </div>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-[16px] font-medium text-gray-900 mb-2">Shipping method</h2>
                                <div className="space-y-1">
                                    {showCardPayment && (
                                        <label className={`flex items-center justify-between p-2 border rounded-lg cursor-pointer transition-all ${formData.shippingMethod === 'prepaid' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                            <div className="flex items-center gap-3">
                                                <input type="radio" name="shippingMethod" value="prepaid" checked={formData.shippingMethod === 'prepaid'} onChange={handleChange} className="w-[14px] h-[14px] text-blue-600 focus:ring-blue-500 cursor-pointer" />
                                                <span className="text-[12px] text-gray-900 font-medium">Prepaid (Free Shipping + Priority Dispatch)</span>
                                            </div>
                                            <span className="text-[12px] font-medium text-gray-900">FREE</span>
                                        </label>
                                    )}
                                    <label className={`flex items-center justify-between p-2 border rounded-lg cursor-pointer transition-all ${formData.shippingMethod === 'cod' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                        <div className="flex items-center gap-3">
                                            <input type="radio" name="shippingMethod" value="cod" checked={formData.shippingMethod === 'cod'} onChange={handleChange} className="w-[14px] h-[14px] text-blue-600 focus:ring-blue-500 cursor-pointer" />
                                            <div>
                                                <span className="text-[12px] text-gray-900 font-medium block">COD Flat Rs {settings.shippingCost} - Ecom Taxes + Cash On Delivery Charges</span>
                                                <span className="text-[11px] text-gray-500">Free For Rs {settings.freeShippingThreshold}+ Orders</span>
                                            </div>
                                        </div>
                                        <span className="text-[12px] font-medium text-gray-900">Rs {settings.shippingCost}.00</span>
                                    </label>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-[16px] font-medium text-gray-900 mb-1">Payment</h2>
                                <p className="text-[11px] text-gray-500 mb-2">All transactions are secure and encrypted.</p>
                                <div className="space-y-1">
                                    {showCardPayment && (
                                        <label className={`relative block border rounded-lg cursor-pointer transition-all ${formData.paymentMethod === 'card' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                            <input type="radio" name="paymentMethod" value="card" checked={formData.paymentMethod === 'card'} onChange={handleChange} className="sr-only" />
                                            <div className="flex items-center justify-between p-2">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-3.5 h-3.5 border-2 rounded-full flex items-center justify-center ${formData.paymentMethod === 'card' ? 'border-blue-600' : 'border-gray-300'}`}>
                                                        {formData.paymentMethod === 'card' && <div className="w-2 h-2 rounded-full bg-blue-600"></div>}
                                                    </div>
                                                    <span className="text-[12px] text-gray-900 font-medium">Debit - Credit Card</span>
                                                </div>
                                                <div className="flex gap-1">
                                                    <VisaIcon />
                                                    <MastercardIcon />
                                                </div>
                                            </div>
                                            {formData.paymentMethod === 'card' && (
                                                <div className="p-2 pt-0 text-[12px] text-gray-600">
                                                    You'll be redirected to Debit - Credit Card to complete your purchase.
                                                </div>
                                            )}
                                        </label>
                                    )}

                                    <label className={`relative block border rounded-lg cursor-pointer transition-all ${formData.paymentMethod === 'banktransfer' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                        <input type="radio" name="paymentMethod" value="banktransfer" checked={formData.paymentMethod === 'banktransfer'} onChange={handleChange} className="sr-only" />
                                        <div className="flex items-center justify-between p-2">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-3.5 h-3.5 border-2 rounded-full flex items-center justify-center ${formData.paymentMethod === 'banktransfer' ? 'border-blue-600' : 'border-gray-300'}`}>
                                                    {formData.paymentMethod === 'banktransfer' && <div className="w-2 h-2 rounded-full bg-blue-600"></div>}
                                                </div>
                                                <span className="text-[12px] text-gray-900 font-medium">Bank Transfer</span>
                                            </div>
                                            <div className="flex gap-1">
                                                <FiCheckCircle className="text-gray-300" size={14} />
                                            </div>
                                        </div>
                                        {formData.paymentMethod === 'banktransfer' && (
                                            <div className="p-2 pt-0 text-[12px] text-gray-600">
                                                <div className="mt-2 bg-gray-50 p-3 rounded text-sm">
                                                    <p><strong>Bank:</strong> {bankDetails.bankName}</p>
                                                    <p><strong>Branch:</strong> {bankDetails.branchName} ({bankDetails.branchCode})</p>
                                                    <p><strong>Account Title:</strong> {bankDetails.title}</p>
                                                    <p><strong>Account Number:</strong> {bankDetails.accountNumber}</p>
                                                    <p><strong>IBAN:</strong> {bankDetails.iban}</p>
                                                    <p><strong>Swift Code:</strong> {bankDetails.swiftCode}</p>
                                                </div>
                                                <div className="mt-3">
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">Upload Payment Receipt</label>
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleReceiptUpload}
                                                        disabled={uploadingReceipt}
                                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                                    />
                                                    {uploadingReceipt && <span className="text-xs text-blue-600 mt-1">Uploading...</span>}
                                                    {receiptUrl && <span className="text-xs text-green-600 mt-1">✓ Receipt uploaded</span>}
                                                </div>
                                            </div>
                                        )}
                                    </label>

                                    {formData.shippingMethod === 'cod' && (
                                        <label className={`relative block border rounded-lg cursor-pointer transition-all ${formData.paymentMethod === 'cod' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                            <input type="radio" name="paymentMethod" value="cod" checked={formData.paymentMethod === 'cod'} onChange={handleChange} className="sr-only" />
                                            <div className="flex items-center justify-between p-2">
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-3.5 h-3.5 border-2 rounded-full flex items-center justify-center ${formData.paymentMethod === 'cod' ? 'border-blue-600' : 'border-gray-300'}`}>
                                                        {formData.paymentMethod === 'cod' && <div className="w-2 h-2 rounded-full bg-blue-600"></div>}
                                                    </div>
                                                    <span className="text-[12px] text-gray-900 font-medium">Cash on Delivery</span>
                                                </div>
                                                <div className="flex gap-1">
                                                    <FiCheckCircle className="text-gray-300" size={14} />
                                                </div>
                                            </div>
                                        </label>
                                    )}
                                </div>
                            </section>

                            <section>
                                <h2 className="text-[16px] font-medium text-gray-900 mb-2">Billing address</h2>
                                <div className="space-y-1">
                                    <label className={`flex items-center gap-3 p-2 border rounded-lg cursor-pointer transition-all ${formData.billingAddress === 'same' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                        <input type="radio" name="billingAddress" value="same" checked={formData.billingAddress === 'same'} onChange={handleChange} className="w-[14px] h-[14px] text-blue-600 focus:ring-blue-500 cursor-pointer" />
                                        <span className="text-[12px] text-gray-900 font-medium">Same as shipping address</span>
                                    </label>
                                    <label className={`flex items-center gap-3 p-2 border rounded-lg cursor-pointer transition-all ${formData.billingAddress === 'different' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`}>
                                        <input type="radio" name="billingAddress" value="different" checked={formData.billingAddress === 'different'} onChange={handleChange} className="w-[14px] h-[14px] text-blue-600 focus:ring-blue-500 cursor-pointer" />
                                        <span className="text-[12px] text-gray-900 font-medium">Use a different billing address</span>
                                    </label>
                                </div>

                                {formData.billingAddress === 'different' && (
                                    <div className="mt-4 space-y-2 pt-4 border-t border-gray-200">
                                        <div className="relative">
                                            <label className="absolute top-[4px] left-3 text-[9px] text-gray-500 pointer-events-none">Country/Region</label>
                                            <select name="billingCountry" value={billingFormData.billingCountry} onChange={handleBillingChange} className="w-full border border-[#d9d9d9] rounded-[3px] pt-[18px] pb-2 px-3 text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none appearance-none bg-white">
                                                <option value="Pakistan">Pakistan</option>
                                            </select>
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"><svg className="w-2.5 h-2.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg></div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="relative">
                                                <input type="text" name="billingFirstName" value={billingFormData.billingFirstName} onChange={handleBillingChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                                <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">First name</label>
                                            </div>
                                            <div className="relative">
                                                <input type="text" name="billingLastName" value={billingFormData.billingLastName} onChange={handleBillingChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                                <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Last name</label>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <input type="text" name="billingAddress" value={billingFormData.billingAddress} onChange={handleBillingChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                            <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Address</label>
                                        </div>
                                        <div className="relative">
                                            <input type="text" name="billingApartment" value={billingFormData.billingApartment} onChange={handleBillingChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                            <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Apartment, suite, etc. (optional)</label>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="relative">
                                                <input type="text" name="billingCity" value={billingFormData.billingCity} onChange={handleBillingChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                                <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">City</label>
                                            </div>
                                            <div className="relative">
                                                <input type="text" name="billingPostalCode" value={billingFormData.billingPostalCode} onChange={handleBillingChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                                <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Postal code (optional)</label>
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <input type="tel" name="billingPhone" value={billingFormData.billingPhone} onChange={handleBillingChange} placeholder=" " className="w-full border border-[#d9d9d9] rounded-[3px] p-2 pt-[18px] text-[12px] focus:ring-1 focus:ring-[#e81e82] focus:border-transparent outline-none peer transition-all" />
                                            <label className="absolute left-3 top-[4px] text-[9px] text-gray-500 transition-all peer-placeholder-shown:text-[12px] peer-placeholder-shown:top-[10px] peer-focus:top-[4px] peer-focus:text-[9px] pointer-events-none">Phone (optional)</label>
                                        </div>
                                    </div>
                                )}
                            </section>

                            <button
                                onClick={handlePlaceOrder}
                                disabled={loading}
                                className="w-full py-2.5 text-white text-[14px] font-semibold rounded-[4px] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                                style={{ backgroundColor: 'var(--primary-color, #e81e82)' }}
                            >
                                {loading ? 'Processing...' : (formData.paymentMethod === 'card' ? 'Pay now' : 'Complete Order')}
                            </button>

                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500">
                                    <Link to="/returns" className="hover:text-[var(--primary-color)] underline">Refund policy</Link>
                                    <Link to="/shipping-policy" className="hover:text-[var(--primary-color)] underline">Shipping</Link>
                                    <Link to="/privacy-policy" className="hover:text-[var(--primary-color)] underline">Privacy policy</Link>
                                    <Link to="/terms" className="hover:text-[var(--primary-color)] underline">Terms of service</Link>
                                    <Link to="/contact" className="hover:text-[var(--primary-color)] underline">Contact</Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Desktop summary - sticky */}
                <div className="hidden lg:block lg:flex-1 bg-[#faf4f8] py-6 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto checkout-right-scroll" style={{ backgroundColor: settings.checkoutSidebarColor }}>
                    {summaryContent}
                </div>
            </div>

            <style>{`
                .checkout-left-scroll::-webkit-scrollbar,
                .checkout-right-scroll::-webkit-scrollbar {
                    display: none !important;
                }
                .checkout-left-scroll,
                .checkout-right-scroll {
                    -ms-overflow-style: none !important;
                    scrollbar-width: none !important;
                }
                @media (max-width: 1023px) {
                    .checkout-right-scroll {
                        padding-bottom: 20px;
                    }
                }
            `}</style>
        </div>
    );
};

export default CheckoutPage;